import { useCallback, useMemo } from 'react';
import { IconSelectFilled } from '@direct-frontend/components';
import { VersionSelect } from '#shared/components/VersionSelect';
import { getRequestHttpStatus } from '#shared/lib/errorBooster';
import { logError } from '#shared/lib/rum';
import { format } from '#shared/lib/string';
import { constructorTestIds } from '#shared/lib/testids';
import { showError } from '#shared/lib/toast';
import { getVersion, useGetAllVersions } from '../../api';
import { useApplyConstructorVersion } from '../../hooks/useApplyConstructorVersion';
import { useConstructorMigrateGlobalSettings } from '../../hooks/useConstructorMigrateGlobalSettings';
import { useConstructorActiveVersion } from '../../model/slices';

export const ConstructorVersionSelect = () => {
    const { data } = useGetAllVersions({});

    const activeVersion = useConstructorActiveVersion();
    const applyConstructorVersion = useApplyConstructorVersion();
    const migrateGlobalSettings = useConstructorMigrateGlobalSettings();

    // eslint-disable-next-line react-hooks/preserve-manual-memoization -- Автоматический игнор в рамках DIRECT-288723
    const items = useMemo(() => {
        if (!data?.versions) {
            return [];
        }

        return data.versions.map((version, index) => {
            return {
                value: String(version.id),
                content: format(version.created_at, 'pretty-datetime'),
                caption: `${version.author || ''}${version.author && index === 0 ? ' • ' : ''}${index === 0 ? 'Опубликованная версия' : ''}`,
                label: String(version.id),
                iconLeft: index === 0 ? <IconSelectFilled color="green" role="main" size="16" /> : undefined,
            };
        });
    }, [data?.versions]);

    const onChange = useCallback(
        async (versionId: string) => {
            const id = Number(versionId);
            let version: Awaited<ReturnType<typeof getVersion>>;

            try {
                version = await getVersion({ id });
            } catch (error) {
                logError(
                    {
                        level: 'error',
                        message: 'Constructor version content fetch error',
                        ignoreErrorMessage: true,
                        additional: { constructor_version_id: id, http_status: getRequestHttpStatus(error) },
                    },
                    error as Ya.Rum.LoggableError
                );
                showError({ label: 'Не удалось загрузить версию конструктора' }, error);

                return;
            }

            applyConstructorVersion({ ...version, global_settings: migrateGlobalSettings(version.global_settings) });
        },
        [applyConstructorVersion, migrateGlobalSettings]
    );

    return (
        <VersionSelect
            data-testid={constructorTestIds.versionSelect}
            color="normal"
            items={items}
            value={String(activeVersion)}
            onChange={onChange}
        />
    );
};
